import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  cancelOfferCampaignEnrollment,
  generateOfferCampaignCode,
  getOfferCampaignEnrollments,
  getOfferCampaign,
  getOfferCampaignDisplaySummary,
  listAvailableOfferCampaigns,
  lookupOfferCampaignMrn,
  overrideOfferCampaignAdvance,
  verifyOfferCampaignAdvance,
} from '../lib/api/offer-campaigns';
import { AdvanceVerificationStep } from './AdvanceVerificationStep';
import { CampaignBlocked, OfferErrorState } from './OfferErrorState';
import { EligibilityStep, PatientEligibilityCard } from './EligibilityStep';
import { MrnLookupStep } from './MrnLookupStep';
import { OfferCompletionStep } from './OfferCompletionStep';
import { OfferCountdownBadge } from './OfferCountdownBadge';
import { OfferProgressSteps } from './OfferProgressSteps';
import { OfferSlotCounter } from './OfferSlotCounter';
import { enrollmentDate, exportOfferCampaignEnrollmentCsv, formatOfferDateTime, planSelected } from './OfferCampaignAuditPanels';
import { formatOfferCampaignDate, formatOfferCampaignDateTime } from './OfferCampaignDateField';
import type {
  OfferCampaignCodeResponse,
  OfferCampaignConfig,
  OfferCampaignCoupon,
  OfferCampaignDisplaySummary,
  OfferCampaignEnrollmentSummary,
  OfferCampaignLookupResponse,
} from '../types/offer-campaigns';

type WorkflowState =
  | 'LOADING_CAMPAIGN'
  | 'ENTER_MRN'
  | 'LOOKING_UP_MRN'
  | 'NOT_FOUND'
  | 'NOT_ELIGIBLE'
  | 'ELIGIBLE'
  | 'GENERATING_CODE'
  | 'ADVANCE_PENDING'
  | 'VERIFYING_ADVANCE'
  | 'VERIFICATION_FAILED'
  | 'COMPLETED'
  | 'CAMPAIGN_FULL'
  | 'CAMPAIGN_CLOSED'
  | 'CAMPAIGN_INACTIVE'
  | 'CAMPAIGN_BLOCKED'
  | 'NO_CAMPAIGNS'
  | 'CAMPAIGN_LIST_ERROR';

export function OfferCampaignsTab({
  active,
  token,
  centers,
  canEnroll,
  canManage,
  notify,
}: {
  active: boolean;
  token?: string;
  centers: string[];
  canEnroll: boolean;
  canManage: boolean;
  notify?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
}) {
  const [campaign, setCampaign] = useState<OfferCampaignConfig | null>(null);
  const [selectedCampaignKey, setSelectedCampaignKey] = useState('');
  const [summary, setSummary] = useState<OfferCampaignDisplaySummary | null>(null);
  const [availableCampaigns, setAvailableCampaigns] = useState<OfferCampaignConfig[]>([]);
  const [enrollmentRows, setEnrollmentRows] = useState<OfferCampaignEnrollmentSummary[]>([]);
  const [enrollmentPage, setEnrollmentPage] = useState(0);
  const [enrollmentSearch, setEnrollmentSearch] = useState('');
  const [auditRefreshing, setAuditRefreshing] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [mrn, setMrn] = useState('');
  const [lookup, setLookup] = useState<OfferCampaignLookupResponse | null>(null);
  const [code, setCode] = useState<OfferCampaignCodeResponse | null>(null);
  const [coupon, setCoupon] = useState<OfferCampaignCoupon | null>(null);
  const [state, setState] = useState<WorkflowState>('LOADING_CAMPAIGN');
  const [error, setError] = useState<string | null>(null);
  const [campaignListError, setCampaignListError] = useState(false);
  const [selectedCenterName, setSelectedCenterName] = useState('');
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideBusy, setOverrideBusy] = useState(false);
  const [campaignClock, setCampaignClock] = useState(() => Date.now());

  const loadAdmin = useCallback(async () => {
    try {
      const campaignRows = await listAvailableOfferCampaigns(token);
      setCampaignListError(false);
      setAvailableCampaigns(campaignRows);
      setSelectedCampaignKey((current) => {
        if (current && campaignRows.some((row) => row.campaignKey === current)) return current;
        return campaignRows.find((row) => row.status === 'ACTIVE')?.campaignKey || campaignRows[0]?.campaignKey || '';
      });
      if (campaignRows.length === 0) setState('NO_CAMPAIGNS');
    } catch {
      setCampaignListError(true);
      setAvailableCampaigns([]);
      setSelectedCampaignKey('');
      setCampaign(null);
      setSummary(null);
      setState('CAMPAIGN_LIST_ERROR');
    }
  }, [token]);

  const loadCampaign = useCallback(async () => {
    if (!selectedCampaignKey) {
      setCampaign(null);
      setSummary(null);
      setState(campaignListError ? 'CAMPAIGN_LIST_ERROR' : 'NO_CAMPAIGNS');
      return;
    }
    setError(null);
    setState('LOADING_CAMPAIGN');
    try {
      const [campaignResult, summaryResult] = await Promise.all([
        getOfferCampaign(selectedCampaignKey, token),
        getOfferCampaignDisplaySummary(selectedCampaignKey, token),
      ]);
      const actorScopedCampaign = availableCampaigns.find((row) => row.campaignKey === selectedCampaignKey);
      const effectiveCampaign = actorScopedCampaign
        ? { ...campaignResult, eligibleCenters: actorScopedCampaign.eligibleCenters, themeConfig: actorScopedCampaign.themeConfig }
        : campaignResult;
      setCampaign(effectiveCampaign);
      setSummary(summaryResult);
      setState(initialStateForCampaign(effectiveCampaign, summaryResult));
    } catch {
      setError('Campaign details could not be loaded. No enrollment was started. Please refresh the campaign details and try again.');
      setState('CAMPAIGN_BLOCKED');
    }
  }, [availableCampaigns, campaignListError, selectedCampaignKey, token]);

  const bookingWindow = useMemo(() => campaignWindowAvailability(campaign, new Date(campaignClock)), [campaign, campaignClock]);
  const accessibleCenters = useMemo(() => filterCampaignCenters(centers, campaign, bookingWindow.active), [bookingWindow.active, campaign, centers]);
  const centerOptions = accessibleCenters;
  const activeCenterName = useMemo(() => {
    if (selectedCenterName && centerOptions.some((center) => centersMatchText(center, selectedCenterName))) return selectedCenterName;
    return centerOptions[0] || '';
  }, [centerOptions, selectedCenterName]);

  useEffect(() => {
    if (!active) return;
    void loadAdmin();
  }, [active, loadAdmin]);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setCampaignClock(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [active]);

  useEffect(() => {
    if (!campaign) return;
    setState((current) => ['ENTER_MRN', 'CAMPAIGN_CLOSED'].includes(current)
      ? initialStateForCampaign(campaign, summary, new Date(campaignClock))
      : current);
  }, [campaign, campaignClock, summary]);

  useEffect(() => {
    if (!active || !selectedCampaignKey) return;
    void loadCampaign();
  }, [active, selectedCampaignKey, loadCampaign]);

  const loadAudit = useCallback(async () => {
    if (!campaign || !selectedCampaignKey || !activeCenterName) {
      setEnrollmentRows([]);
      return;
    }
    const enrollments = await getOfferCampaignEnrollments(selectedCampaignKey, { centerName: activeCenterName }, token);
    setEnrollmentRows(enrollments);
  }, [activeCenterName, campaign, selectedCampaignKey, token]);

  useEffect(() => {
    if (!active || !campaign || !selectedCampaignKey || !activeCenterName) return;
    void loadAudit().catch((err) => notify?.(businessErrorMessage(err, 'Campaign enrollments could not be loaded. Please refresh the enrollment list.'), 'error'));
  }, [active, activeCenterName, campaign, selectedCampaignKey, loadAudit, notify]);

  const refreshSummary = useCallback(async () => {
    if (!selectedCampaignKey) return;
    try {
      setSummary(await getOfferCampaignDisplaySummary(selectedCampaignKey, token));
    } catch (err) {
      notify?.(businessErrorMessage(err, 'Campaign progress could not be refreshed. Please try again.'), 'error');
    }
  }, [notify, selectedCampaignKey, token]);

  const refreshEnrollments = useCallback(async () => {
    setAuditRefreshing(true);
    try {
      await loadAudit();
    } catch (err) {
      notify?.(businessErrorMessage(err, 'Campaign enrollments could not be refreshed. Please try again.'), 'error');
    } finally {
      setAuditRefreshing(false);
    }
  }, [loadAudit, notify]);

  const resetWorkflow = () => {
    setMrn('');
    setLookup(null);
    setCode(null);
    setCoupon(null);
    setError(null);
    setState(campaign ? initialStateForCampaign(campaign, summary) : campaignListError ? 'CAMPAIGN_LIST_ERROR' : 'NO_CAMPAIGNS');
  };

  const changeCampaign = (campaignKey: string) => {
    setSelectedCampaignKey(campaignKey);
    setSelectedCenterName('');
    setMrn('');
    setLookup(null);
    setCode(null);
    setCoupon(null);
    setError(null);
    setCampaign(null);
    setSummary(null);
    setState('LOADING_CAMPAIGN');
  };

  const backToMrn = () => {
    setError(null);
    setCode(null);
    setCoupon(null);
    setState('ENTER_MRN');
  };

  const backToEligibility = () => {
    setError(null);
    setCoupon(null);
    setState('ELIGIBLE');
  };

  const runLookup = async () => {
    const normalizedMrn = mrn.trim();
    if (!canEnroll) return setError('Your access is view-only. Ask an administrator for permission to enroll patients.');
    if (!selectedCampaignKey) return setError('Select an offer campaign before checking the patient.');
    if (!normalizedMrn) return setError('Enter the patient MRN before checking eligibility.');
    if (!activeCenterName) return setError('No eligible center is available for this campaign. Select another campaign or contact the campaign administrator.');
    setState('LOOKING_UP_MRN');
    setLookup(null);
    setCode(null);
    setCoupon(null);
    setError(null);
    try {
      const result = await lookupOfferCampaignMrn(selectedCampaignKey, normalizedMrn, activeCenterName, token);
      setLookup(result);
      if (result.completedEnrollment?.coupon) {
        setCoupon(result.completedEnrollment.coupon);
        setState('COMPLETED');
      } else if (result.activeReservation) {
        const activeReservation = result.activeReservation;
        setCode(activeReservation);
        setLookup((current) => current ? {
          ...current,
          eligible: true,
          discountAmount: activeReservation.discountAmount,
          discountAmountFormatted: activeReservation.discountAmountFormatted,
          discountFormulaLabel: activeReservation.discountFormulaLabel,
        } : current);
        setState('ADVANCE_PENDING');
      } else if (!result.found) setState('NOT_FOUND');
      else if (!result.eligible) setState(result.reason === 'CAMPAIGN_SLOTS_FULL' ? 'CAMPAIGN_FULL' : 'NOT_ELIGIBLE');
      else setState('ELIGIBLE');
      await refreshSummary();
    } catch (err) {
      setError(businessErrorMessage(err, 'The patient could not be checked right now. No enrollment was started. Please try again.'));
      setState(campaign ? initialStateForCampaign(campaign, summary) : 'ENTER_MRN');
    }
  };

  const runGenerateCode = async () => {
    const normalizedMrn = lookup?.mrn || mrn.trim();
    if (!canEnroll) return setError('Your access is view-only. Ask an administrator for permission to enroll patients.');
    if (!selectedCampaignKey) return setError('Select an offer campaign before generating an offer code.');
    if (!normalizedMrn) return;
    setState('GENERATING_CODE');
    setError(null);
    try {
      const result = await generateOfferCampaignCode(selectedCampaignKey, normalizedMrn, lookup?.centerName || activeCenterName, token);
      setCode(result);
      setLookup(current => current ? { ...current, slotsRemaining: result.slotsRemaining, campaignLimit: result.campaignLimit } : current);
      setState('ADVANCE_PENDING');
      await Promise.all([refreshSummary(), loadAudit()]);
    } catch (err) {
      setError(businessErrorMessage(err, 'The offer code could not be generated. No enrollment was completed. Please try again.'));
      setState('ELIGIBLE');
    }
  };

  const runVerifyAdvance = async () => {
    if (!code?.enrollmentId || !code.offerCode) return;
    if (!canEnroll) return setError('Your access is view-only. Ask an administrator for permission to verify advance payments.');
    if (!selectedCampaignKey) return setError('Select an offer campaign before verifying the advance payment.');
    setState('VERIFYING_ADVANCE');
    setError(null);
    try {
      const result = await verifyOfferCampaignAdvance(selectedCampaignKey, {
        enrollmentId: code.enrollmentId,
        offerCode: code.offerCode,
        mrn: lookup?.mrn || mrn.trim(),
      }, token);
      if (result.verified && result.coupon) {
        setCoupon(result.coupon);
        setState('COMPLETED');
        await Promise.all([refreshSummary(), loadAudit()]);
      } else {
        setError(advanceVerificationMessages(
          result.failureReasons || (result.failureReason ? [result.failureReason] : []),
          lookup,
          code,
          result.verifiedPayment,
          campaign,
        ));
        setState('VERIFICATION_FAILED');
      }
    } catch (err) {
      setError(businessErrorMessage(err, 'The advance payment could not be checked right now. Please wait a moment and try again.'));
      setState('VERIFICATION_FAILED');
    }
  };

  const runCancelEnrollment = async () => {
    if (!code?.enrollmentId || !code.offerCode || !selectedCampaignKey) return;
    setError(null);
    try {
      await cancelOfferCampaignEnrollment(selectedCampaignKey, {
        enrollmentId: code.enrollmentId,
        offerCode: code.offerCode,
        mrn: lookup?.mrn || mrn.trim(),
      }, token);
      await Promise.all([refreshSummary(), loadAudit()]);
      resetWorkflow();
    } catch (err) {
      setError(businessErrorMessage(err, 'The enrollment could not be cancelled. Its current status has not been changed. Please try again.'));
    }
  };

  const runAdminOverride = async (reason: string) => {
    if (!code?.enrollmentId || !code.offerCode || !selectedCampaignKey) return;
    setOverrideBusy(true);
    setError(null);
    try {
      const result = await overrideOfferCampaignAdvance(selectedCampaignKey, {
        enrollmentId: code.enrollmentId,
        offerCode: code.offerCode,
        mrn: lookup?.mrn || mrn.trim(),
        reason,
        confirm: true,
      }, token);
      if (!result.coupon) throw new Error('The override was completed but the coupon could not be loaded.');
      setCoupon(result.coupon);
      setOverrideOpen(false);
      setState('COMPLETED');
      await Promise.all([refreshSummary(), loadAudit()]);
    } catch (err) {
      setError(businessErrorMessage(err, 'The approved override could not be completed. The enrollment remains unchanged. Please try again.'));
    } finally {
      setOverrideBusy(false);
    }
  };

  const activeStep = useMemo(() => workflowStepIndex(state), [state]);
  const unlimitedSlots = summary?.unlimitedSlots ?? campaign?.themeConfig?.unlimitedSlots === true;
  const slotsRemaining = summary?.slotsRemaining ?? null;
  const slotLimit = summary?.totalLimit ?? lookup?.campaignLimit ?? code?.campaignLimit ?? campaign?.totalSlotLimit ?? 0;
  const selectedCampaignTitle = campaign?.campaignName || 'Offers and Campaigns';
  const selectedCampaignSubtitle = shortCampaignDescription(campaign);
  const activeCenterSummary = useMemo(() => findCenterSummary(summary?.centers || [], activeCenterName), [activeCenterName, summary?.centers]);
  const centerCompleted = activeCenterSummary?.completedCount ?? 0;
  const centerSlotsRemaining = unlimitedSlots ? null : Math.max((slotLimit || campaign?.totalSlotLimit || 0) - centerCompleted, 0);
  const campaignPeriod = formatCampaignPeriod(campaign);
  const campaignRuleSummary = useMemo(() => summarizeCampaignRules(campaign, bookingWindow, centerOptions.length), [bookingWindow, campaign, centerOptions.length]);
  const campaignStateMessage = useMemo(() => describeCampaignState(campaign, state, bookingWindow), [bookingWindow, campaign, state]);
  const enrollmentPageSize = 15;
  const filteredEnrollmentRows = useMemo(() => {
    const query = enrollmentSearch.trim().toLowerCase();
    if (!query) return enrollmentRows;
    return enrollmentRows.filter((row) => [row.mrn, row.patientName, row.offerCode].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [enrollmentRows, enrollmentSearch]);
  const enrollmentPageCount = Math.max(1, Math.ceil(filteredEnrollmentRows.length / enrollmentPageSize));
  const visibleEnrollmentRows = filteredEnrollmentRows.slice(enrollmentPage * enrollmentPageSize, enrollmentPage * enrollmentPageSize + enrollmentPageSize);

  useEffect(() => {
    setEnrollmentPage(0);
  }, [activeCenterName, selectedCampaignKey, enrollmentSearch]);

  useEffect(() => {
    setEnrollmentPage((current) => Math.min(current, enrollmentPageCount - 1));
  }, [enrollmentPageCount]);

  return (
    <section id="tab-offer-campaigns" className="tab-content offer-campaigns-tab" style={{ display: active ? 'block' : 'none' }}>
      <div className="offer-admin-card offer-campaign-card offer-campaign-action-header">
        <div className="offer-campaign-action-main">
          <p className="offer-campaign-eyebrow">Offers and Campaigns</p>
          <h3>{selectedCampaignTitle}</h3>
          <p>{selectedCampaignSubtitle}</p>
        </div>
        <div className="offer-campaign-action-controls">
          <label className="offer-campaign-picker">
            <span>Select Campaign</span>
            <select value={selectedCampaignKey} onChange={(event) => changeCampaign(event.target.value)} disabled={availableCampaigns.length === 0}>
              {availableCampaigns.length === 0 && <option value="">No campaigns available for your centers</option>}
              {availableCampaigns.map((row) => (
                <option key={row.campaignKey} value={row.campaignKey}>{row.campaignName}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="offer-campaign-grid">
        <div className="offer-campaign-main-card offer-campaign-card">
          <OfferProgressSteps activeStep={activeStep} />
          {error && !['ADVANCE_PENDING', 'VERIFYING_ADVANCE', 'VERIFICATION_FAILED'].includes(state) && <div className="offer-campaign-alert">{error}</div>}
          {state === 'LOADING_CAMPAIGN' && <OfferErrorState title="Loading campaign" text="Please wait while the latest campaign rules and progress are loaded." icon="clock" />}
          {state === 'NO_CAMPAIGNS' && <CampaignBlocked title="No campaigns are available for your centers" text="Only campaigns that include one of your permitted centers appear here. Contact an administrator if you believe a campaign is missing." icon="calendar" onRefresh={loadAdmin} />}
          {state === 'CAMPAIGN_LIST_ERROR' && <CampaignBlocked title="Campaign list could not be loaded" text="No enrollment was started. Check your connection and refresh the campaign list." onRefresh={loadAdmin} />}
          {state === 'CAMPAIGN_BLOCKED' && <CampaignBlocked {...campaignStateMessage} onRefresh={loadCampaign} />}
          {state === 'CAMPAIGN_INACTIVE' && <CampaignBlocked {...campaignStateMessage} onRefresh={loadCampaign} />}
          {state === 'CAMPAIGN_FULL' && <OfferErrorState title="Campaign capacity has been reached" text="All available enrollment places are currently in use. Refresh the campaign details in case an unfinished reservation has expired." icon="ticket" />}
          {state === 'CAMPAIGN_CLOSED' && <CampaignBlocked {...campaignStateMessage} onRefresh={loadCampaign} />}
          {!canEnroll && <OfferErrorState title="View-only access" text="You can review campaign status and enrollments, but you cannot check patients, generate offer codes, or verify advance payments. Ask an administrator if you need enrollment access." icon="ticket" />}
          {['ENTER_MRN', 'LOOKING_UP_MRN', 'NOT_FOUND', 'NOT_ELIGIBLE', 'ELIGIBLE', 'GENERATING_CODE'].includes(state) && (
            <div className="offer-enrollment-panel">
              <MrnLookupStep mrn={mrn} busy={state === 'LOOKING_UP_MRN'} disabled={!canEnroll} onMrnChange={setMrn} onCheck={runLookup} onClear={resetWorkflow} />
              {lookup?.found && !['ELIGIBLE', 'GENERATING_CODE'].includes(state) && <PatientEligibilityCard lookup={lookup} />}
              {state === 'NOT_FOUND' && <OfferErrorState title="Patient not found" text={`No patient record was found for MRN ${lookup?.mrn || mrn.trim()} at ${activeCenterName || 'the selected center'}. Check the MRN and selected center, then try again.`} />}
              {state === 'NOT_ELIGIBLE' && <OfferErrorState title="Patient not eligible" text={eligibilityFailureMessage(lookup?.reason)} />}
              {['ELIGIBLE', 'GENERATING_CODE'].includes(state) && lookup && <EligibilityStep lookup={lookup} generating={state === 'GENERATING_CODE'} canEnroll={canEnroll} onGenerate={runGenerateCode} onBack={backToMrn} onCancel={resetWorkflow} />}
            </div>
          )}
          {['ADVANCE_PENDING', 'VERIFYING_ADVANCE', 'VERIFICATION_FAILED'].includes(state) && code && lookup && (
            <AdvanceVerificationStep code={code} lookup={lookup} busy={state === 'VERIFYING_ADVANCE'} canEnroll={canEnroll} error={state === 'VERIFICATION_FAILED' ? error : null} onVerify={runVerifyAdvance} onRetry={runVerifyAdvance} onBack={backToEligibility} onCancel={runCancelEnrollment} canAdminOverride={canManage && campaign?.themeConfig?.allowAdminOverride === true && state === 'VERIFICATION_FAILED'} onOpenAdminOverride={() => setOverrideOpen(true)} />
          )}
          {state === 'COMPLETED' && coupon && <OfferCompletionStep coupon={coupon} displayRouteSlug={campaign?.displayRouteSlug} onNext={resetWorkflow} token={token} notify={notify} />}
        </div>
        <aside className="offer-campaign-side-card">
          {centerOptions.length > 1 && (
            <label className="offer-campaign-side-select">
              <span>Select Center</span>
              <select value={activeCenterName} onChange={(event) => setSelectedCenterName(event.target.value)}>
                {centerOptions.map((center) => (
                  <option key={center} value={center}>{center}</option>
                ))}
              </select>
            </label>
          )}
          <h4>Center Progress</h4>
          <OfferSlotCounter label={activeCenterName || 'Selected Center'} slotsRemaining={centerSlotsRemaining} completed={centerCompleted} total={slotLimit} unlimited={unlimitedSlots} />
          <h4>Overall Campaign</h4>
          <OfferSlotCounter label="Overall Campaign" slotsRemaining={slotsRemaining} completed={summary?.totalCompleted ?? 0} total={slotLimit} unlimited={unlimitedSlots} />
          <h4>Campaign Rules</h4>
          <dl>
            <div><dt>Status</dt><dd>{campaign?.status || '-'}</dd></div>
            <div><dt>Discount Rule</dt><dd>{formatDiscountRule(campaign)}</dd></div>
            <div><dt>Slot Limit</dt><dd>{unlimitedSlots ? 'Unlimited' : `${slotLimit || campaign?.totalSlotLimit || '-'} total slots`}</dd></div>
            <div><dt>Campaign Period</dt><dd>{campaignPeriod}</dd></div>
            <div><dt>Booking Window</dt><dd>{campaignRuleSummary.bookingWindow}</dd></div>
            <div><dt>Eligible Centers</dt><dd>{campaignRuleSummary.centers}</dd></div>
            <div><dt>Patient Rule</dt><dd>{campaignRuleSummary.patientRule}</dd></div>
            <div><dt>Required Advance</dt><dd>{campaignRuleSummary.advance}</dd></div>
            <div><dt>Coupon Validity</dt><dd>{campaignRuleSummary.couponValidity}</dd></div>
            <div><dt>Treatment Start</dt><dd>{campaignRuleSummary.treatmentStart}</dd></div>
          </dl>
        </aside>
      </div>

      <CampaignEnrollmentsSection
        rows={visibleEnrollmentRows}
        totalRows={filteredEnrollmentRows.length}
        campaignTotalRows={enrollmentRows.length}
        page={enrollmentPage}
        pageCount={enrollmentPageCount}
        search={enrollmentSearch}
        refreshing={auditRefreshing}
        onPage={setEnrollmentPage}
        onSearch={setEnrollmentSearch}
        onRefresh={() => void refreshEnrollments()}
        onExport={() => setExportModalOpen(true)}
      />

      {exportModalOpen && (
        <EnrollmentExportModal
          visibleRows={visibleEnrollmentRows}
          allRows={filteredEnrollmentRows}
          search={enrollmentSearch}
          onClose={() => setExportModalOpen(false)}
        />
      )}
      {overrideOpen && <OfferAdvanceOverrideModal busy={overrideBusy} onClose={() => setOverrideOpen(false)} onSubmit={runAdminOverride} />}
    </section>
  );
}

function OfferAdvanceOverrideModal({ busy, onClose, onSubmit }: { busy: boolean; onClose: () => void; onSubmit: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const valid = reason.trim().length >= 5 && confirmed;
  return (
    <div className="offer-override-dialog-backdrop" role="presentation">
      <div className="offer-override-dialog" role="dialog" aria-modal="true" aria-labelledby="offer-override-title">
        <button className="offer-override-close" type="button" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        <span className="offer-override-kicker">Superadmin action</span>
        <h3 id="offer-override-title">Continue to coupon by admin override</h3>
        <p>This bypasses only this failed payment verification. The reason, user, date, and time will be recorded in the campaign audit trail.</p>
        <label><span>Override reason</span><textarea value={reason} rows={4} placeholder="Example: Screenshot review — payment record is intentionally not available." onChange={(event) => setReason(event.target.value)} disabled={busy} /></label>
        <label className="offer-override-confirm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={busy} /><span>I confirm this is an intentional, audited admin override.</span></label>
        <div className="offer-override-actions"><button className="btn btn-outline" type="button" onClick={onClose} disabled={busy}>Cancel</button><button className="btn btn-primary" type="button" disabled={!valid || busy} onClick={() => onSubmit(reason.trim())}>{busy ? 'Completing…' : 'Complete and open coupon'}</button></div>
      </div>
    </div>
  );
}

function CampaignEnrollmentsSection({
  rows,
  totalRows,
  campaignTotalRows,
  page,
  pageCount,
  search,
  refreshing,
  onPage,
  onSearch,
  onRefresh,
  onExport,
}: {
  rows: OfferCampaignEnrollmentSummary[];
  totalRows: number;
  campaignTotalRows: number;
  page: number;
  pageCount: number;
  search: string;
  refreshing: boolean;
  onPage: (page: number) => void;
  onSearch: (value: string) => void;
  onRefresh: () => void;
  onExport: () => void;
}) {
  return (
    <div className="offer-admin-card offer-enrollments-card">
      <div className="enterprise-card-heading">
        <div>
          <strong>Campaign Enrollments</strong>
          <span>{search ? `${totalRows} matching enrollments out of ${campaignTotalRows}` : `${campaignTotalRows} enrollments for the selected campaign`}</span>
        </div>
        <div className="row-actions offer-enrollment-toolbar">
          <label className="offer-enrollment-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5" /><path d="m16 16 4.5 4.5" /></svg>
            <input value={search} onChange={(event) => onSearch(event.target.value)} placeholder="Search MRN, patient or offer code" aria-label="Search enrollments" />
          </label>
          <button className="mini-btn offer-refresh-btn" onClick={onRefresh} disabled={refreshing} aria-live="polite">
            <span className={refreshing ? 'offer-refresh-icon is-spinning' : 'offer-refresh-icon'} aria-hidden="true">↻</span>{refreshing ? 'Refreshing...' : 'Refresh Enrollments'}
          </button>
          <button className="mini-btn" onClick={onExport} disabled={!totalRows}>Export Excel</button>
          <button className="mini-btn" disabled={page === 0} onClick={() => onPage(Math.max(0, page - 1))}>Previous</button>
          <span className="offer-enrollment-page-status">{totalRows ? `${page + 1} / ${pageCount}` : '0 / 0'}</span>
          <button className="mini-btn" disabled={page >= pageCount - 1} onClick={() => onPage(Math.min(pageCount - 1, page + 1))}>Next</button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="enterprise-table offer-enrollment-table">
          <thead>
            <tr>
              <th>Center</th><th>MRN</th><th>Patient</th><th>Offer Code</th><th>Status</th><th>Created By</th><th>Enrollment Date</th><th>Plan Selected</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.centerName || '-'}</td>
                <td>{row.mrn || '-'}</td>
                <td>{row.patientName || '-'}</td>
                <td>{row.offerCode || '-'}</td>
                <td><span className={`status-pill ${row.status.toLowerCase()}`}>{row.status}</span></td>
                <td>{row.createdUsername || '-'}</td>
                <td>{formatOfferDateTime(enrollmentDate(row))}</td>
                <td>{planSelected(row)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="empty-cell">{search ? 'No enrollments match this MRN, patient name, or offer code.' : 'No patients have been enrolled in this campaign for the selected center.'}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EnrollmentExportModal({ visibleRows, allRows, search, onClose }: { visibleRows: OfferCampaignEnrollmentSummary[]; allRows: OfferCampaignEnrollmentSummary[]; search: string; onClose: () => void }) {
  return (
    <div className="modal-overlay offer-admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal offer-export-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <button onClick={onClose} className="admin-modal-close">&times;</button>
        <div className="offer-export-modal-heading">
          <span className="offer-export-modal-icon" aria-hidden="true">⇩</span>
          <div><h3>Export campaign enrollments</h3><p>Choose the data range to download as a spreadsheet-ready CSV file.</p></div>
        </div>
        {search && <p className="offer-export-search-note">Your active search, “{search}”, will be included in this export.</p>}
        <div className="offer-export-options">
          <button className="offer-export-choice" disabled={!visibleRows.length} onClick={() => { exportOfferCampaignEnrollmentCsv(visibleRows, 'offer-campaign-enrollments-visible-page.csv'); onClose(); }}><strong>Current page</strong><span>{visibleRows.length} enrollment{visibleRows.length === 1 ? '' : 's'} currently visible</span></button>
          <button className="offer-export-choice is-primary" disabled={!allRows.length} onClick={() => { exportOfferCampaignEnrollmentCsv(allRows, 'offer-campaign-enrollments-full-report.csv'); onClose(); }}><strong>All matching enrollments</strong><span>{allRows.length} enrollment{allRows.length === 1 ? '' : 's'} in this report</span></button>
        </div>
        <div className="offer-export-modal-footer">
          <span>CSV files open directly in Excel.</span><button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function isRunnable(campaign: OfferCampaignConfig) {
  const requiredSourceTypes = ['PATIENT_REGISTRATION', 'ADVANCE_PAYMENT'];

  return campaign.status === 'ACTIVE' && requiredSourceTypes.every(sourceType => (
    campaign.sources.some(source => (
      source.sourceType === sourceType
      && source.isActive
      && source.isConfigured
    ))
  ));
}

function initialStateForCampaign(campaign: OfferCampaignConfig, summary: OfferCampaignDisplaySummary | null, now = new Date()): WorkflowState {
  if (campaign.status !== 'ACTIVE') return 'CAMPAIGN_INACTIVE';
  if (!isRunnable(campaign)) return 'CAMPAIGN_BLOCKED';
  if (!campaignWindowAvailability(campaign, now).open) return 'CAMPAIGN_CLOSED';
  if (summary && !summary.unlimitedSlots
    && summary.slotsRemaining !== null
    && summary.slotsRemaining !== undefined
    && summary.slotsRemaining <= 0) return 'CAMPAIGN_FULL';
  return 'ENTER_MRN';
}

function describeCampaignState(
  campaign: OfferCampaignConfig | null,
  state: WorkflowState,
  windowState: CampaignWindowAvailability,
) {
  if (!campaign) {
    return {
      title: 'Campaign details could not be loaded',
      text: 'No enrollment was started. Refresh the campaign details and try again.',
      icon: 'warning' as const,
    };
  }

  const now = Date.now();
  const campaignStarts = dateTimestamp(campaign.startsAt);
  const campaignEnds = dateTimestamp(campaign.endsAt);
  const nextOpening = windowState.next?.startsAt ?? (campaignStarts !== null && campaignStarts > now ? campaign.startsAt : null);

  if (state === 'CAMPAIGN_INACTIVE') {
    if (campaign.status === 'PAUSED') {
      return {
        title: 'Enrollment is temporarily paused',
        text: nextOpening
          ? `The next scheduled booking time is ${formatRuleDateTime(nextOpening)}, but enrollment will remain unavailable until an administrator resumes this campaign.`
          : 'An administrator has paused this campaign. Existing enrollment records are safe, but new patients cannot be enrolled until it is resumed.',
        icon: 'clock' as const,
      };
    }
    if (campaign.status === 'ARCHIVED' || (campaignEnds !== null && campaignEnds < now)) {
      return {
        title: 'This campaign has ended',
        text: campaignEnds !== null
          ? `The campaign closed on ${formatRuleDateTime(campaign.endsAt)}. New enrollments are no longer accepted.`
          : 'This campaign is archived and no longer accepts new enrollments.',
        icon: 'calendar' as const,
      };
    }
    if (nextOpening) {
      return {
        title: `Enrollment is scheduled to open on ${formatRuleDateTime(nextOpening)}`,
        text: `The campaign is currently ${friendlyCampaignStatus(campaign.status)}. An administrator must activate it before staff can enroll patients at the scheduled opening time.`,
        icon: 'calendar' as const,
      };
    }
    return {
      title: 'Enrollment is not active',
      text: `This campaign is currently ${friendlyCampaignStatus(campaign.status)}. Ask the campaign administrator to activate it before enrolling patients.`,
      icon: 'clock' as const,
    };
  }

  if (state === 'CAMPAIGN_CLOSED') {
    if (windowState.next) {
      return {
        title: `Enrollment opens on ${formatRuleDateTime(windowState.next.startsAt)}`,
        text: `${bookingWindowLabel(windowState.next.key)} is scheduled from ${formatRuleDateTime(windowState.next.startsAt)} to ${formatRuleDateTime(windowState.next.endsAt)}. No patient can be enrolled before it opens.`,
        icon: 'calendar' as const,
      };
    }
    if (campaignStarts !== null && campaignStarts > now) {
      return {
        title: `Enrollment opens on ${formatRuleDateTime(campaign.startsAt)}`,
        text: `This campaign is active but its enrollment period has not started. Enrollment is scheduled from ${formatRuleDateTime(campaign.startsAt)}${campaign.endsAt ? ` to ${formatRuleDateTime(campaign.endsAt)}` : ''}.`,
        icon: 'calendar' as const,
      };
    }
    return {
      title: 'The enrollment period has ended',
      text: campaignEnds !== null
        ? `The final enrollment window closed on ${formatRuleDateTime(campaign.endsAt)}. Existing enrollments remain available in the campaign enrollment table.`
        : 'All configured booking windows have closed. Existing enrollments remain available in the campaign enrollment table.',
      icon: 'calendar' as const,
    };
  }

  return {
    title: 'Campaign setup is incomplete',
    text: 'Patient enrollment cannot start until the campaign administrator completes and activates both patient and advance-payment sources.',
    icon: 'warning' as const,
  };
}

function friendlyCampaignStatus(status: string): string {
  const value = String(status || '').trim().replaceAll('_', ' ').toLowerCase();
  return value || 'not active';
}

function eligibilityFailureMessage(reason: string | null | undefined): string {
  const messages: Record<string, string> = {
    MULTIPLE_PATIENT_ROWS_FOUND: 'More than one patient record was found for this MRN. Please correct the duplicate patient record in Artisan, then run Check Eligibility again.',
    MISSING_CENTER: 'The patient’s center is missing. Please update the patient’s center in Artisan, then run Check Eligibility again.',
    CENTER_NOT_ELIGIBLE: 'This patient’s center is not included in the selected offer campaign. Select the patient’s eligible center or use a campaign available for that center.',
    REGISTRATION_DATE_REQUIRED: 'The patient registration date is missing. Update the registration record, then check eligibility again.',
    INVALID_REGISTRATION_DATE: 'The patient registration date is invalid. Correct it in the source system, then check eligibility again.',
    REGISTRATION_DATE_NOT_ELIGIBLE: 'This patient was not registered inside the campaign’s new-patient eligibility period.',
    INVALID_AGE: 'The patient age is not valid. Please update the patient’s age or date of birth in Artisan, then run Check Eligibility again.',
    INVALID_DOB: 'The patient date of birth is not valid. Please update it in Artisan, then run Check Eligibility again.',
    MISSING_DOB_AND_AGE: 'Patient age and date of birth are missing. Please update either field in Artisan, then run Check Eligibility again.',
    AGE_BELOW_MINIMUM: 'The patient does not meet the minimum age requirement for this offer.',
    AGE_ABOVE_MAXIMUM: 'The patient exceeds the maximum age permitted for this offer.',
    PRIMARY_DOCTOR_REQUIRED: 'Primary Doctor is missing for this patient. Please update the Primary Doctor in Artisan, then run Check Eligibility again.',
    B2B_B2C_REQUIRED: 'Marketing Channel is missing or invalid for this patient. Please set it to B2B or B2C in Artisan, then run Check Eligibility again.',
  };
  return messages[reason || ''] || 'This patient does not meet the configured campaign rules.';
}

function advanceVerificationMessages(
  reasons: string[],
  lookup: OfferCampaignLookupResponse | null,
  code: OfferCampaignCodeResponse | null,
  verifiedPayment: Record<string, unknown> | null | undefined,
  campaign: OfferCampaignConfig | null,
): string {
  const patient = lookup?.patientName || `MRN ${lookup?.mrn || ''}`.trim();
  const center = lookup?.centerName || 'the selected center';
  const offerCode = code?.offerCode || 'this offer code';
  const plan = textValue(verifiedPayment?.packagePlan);
  const packageStartDate = displayDate(verifiedPayment?.packageStartDate);
  const packageEndDate = displayDate(verifiedPayment?.packageEndDate);
  const paymentDate = displayDate(verifiedPayment?.paymentDate);
  const paymentAmount = moneyValue(verifiedPayment?.paymentAmount);
  const discountApplied = moneyValue(verifiedPayment?.discountApplied);
  const expectedAdvance = money(code?.requiredAdvanceAmount ?? lookup?.discountAmount);
  const expectedDiscount = money(lookup?.discountAmount);
  const allowedPlans = stringList(campaign?.themeConfig?.allowedPackagePlans);
  const packageEndDateMax = displayDate(campaign?.themeConfig?.packageEndDateMax);
  const messages: Record<string, string> = {
    ADVANCE_ROW_NOT_FOUND: `No advance payment has been recorded yet for ${patient} at ${center}. Complete the payment in Artisan using offer code ${offerCode}, then check again.`,
    OFFER_CODE_MISMATCH: `The payment record does not use offer code ${offerCode}. Please enter this exact offer code while recording the advance.`,
    PACKAGE_PLAN_REQUIRED: 'The selected payment record is not linked to a treatment package. Please record the advance against the enrolled package plan.',
    PACKAGE_START_DATE_MISMATCH: packageStartDate ? `Package start date ${packageStartDate} is outside the treatment-start window configured for this campaign.` : 'Package start date is missing. Please update the enrolled package in Artisan.',
    PACKAGE_END_DATE_MISMATCH: packageEndDate ? `Package end date is ${packageEndDate}${packageEndDateMax ? `, which is after the offer limit of ${packageEndDateMax}` : ''}. Please correct the package dates.` : 'Package end date is missing. Please update the enrolled package in Artisan.',
    PAYMENT_DATE_MISMATCH: paymentDate ? `Payment date is ${paymentDate}. The advance must be recorded on the enrollment date.` : 'Payment date is missing. Please record the advance payment in Artisan, then check again.',
    PAYMENT_AMOUNT_MISMATCH: paymentAmount ? `Recorded advance is ${paymentAmount}. The required advance for this offer is ${expectedAdvance}. Please correct the payment amount.` : `Advance amount is missing. Please record the required advance of ${expectedAdvance} in Artisan.`,
    DISCOUNT_MISMATCH: discountApplied ? `Recorded plan discount is ${discountApplied}. This patient’s eligible offer discount is ${expectedDiscount}. Please correct the discount on the plan.` : `Plan discount is missing. Please apply this patient’s eligible offer discount of ${expectedDiscount}.`,
    PLAN_NOT_ALLOWED: plan ? `Plan “${plan}” is not included in this offer. Please select an approved offer plan${allowedPlans.length ? `: ${allowedPlans.join(', ')}` : ''}.` : 'The enrolled plan is missing. Please select an approved offer plan in Artisan.',
    ESCROW_STATUS_MISMATCH: 'The payment has not reached the required deposit status. Please confirm the advance payment has been posted before checking again.',
    ITEM_STATUS_MISMATCH: 'The payment item is not open for this offer. Please review the payment item status in Artisan.',
    TOTAL_DUE_MISMATCH: 'The plan balance does not match the plan fee, approved discount, and advance payment. Please review the plan financial details in Artisan.',
    MULTIPLE_CONFLICTING_ADVANCE_ROWS: `More than one advance-payment record was found for ${patient}. Please keep only the correct record with offer code ${offerCode}, then check again.`,
  };
  if (reasons.some((reason) => reason.startsWith('LOOKER_ADVANCE_LOOKUP_FAILED'))) return `Payment details are temporarily unavailable for ${patient}. Please wait a moment and check the advance again.`;
  const readable = reasons.map((reason) => messages[reason]).filter(Boolean);
  return readable.length ? readable.join(' ') : 'The advance payment could not be verified. Please review the patient, plan, and payment details in Artisan, then try again.';
}

function textValue(value: unknown): string | null {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item || '').trim()).filter(Boolean) : [];
}

function moneyValue(value: unknown): string | null {
  const amount = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(amount) ? money(amount) : null;
}

function money(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value)
    : 'the required amount';
}

function displayDate(value: unknown): string | null {
  const text = textValue(value);
  if (!text) return null;
  return formatOfferCampaignDate(text);
}

function shortCampaignDescription(campaign: OfferCampaignConfig | null): string {
  const description = typeof campaign?.themeConfig?.description === 'string' ? campaign.themeConfig.description.trim() : '';
  const fallback = 'Enroll eligible patients and verify payment.';
  const value = description || fallback;
  return value.length > 96 ? `${value.slice(0, 93).trimEnd()}...` : value;
}

function filterCampaignCenters(centers: string[], campaign: OfferCampaignConfig | null, activeWindow?: CampaignBookingWindow | null): string[] {
  const allowed = Array.isArray(campaign?.eligibleCenters) ? campaign.eligibleCenters.map(String).filter(Boolean) : [];
  const hasWindowSubset = activeWindow?.centerRule?.mode === 'SUBSET';
  const windowCenters = hasWindowSubset
    ? stringList(activeWindow?.centerRule?.eligibleCenters)
    : allowed;
  const effectiveAllowed = hasWindowSubset ? windowCenters : allowed;
  if (hasWindowSubset && effectiveAllowed.length === 0) return [];
  const baseCenters = centers.length > 0 ? centers : effectiveAllowed;
  if (effectiveAllowed.length === 0) return baseCenters;
  return baseCenters.filter((center) => effectiveAllowed.some((allowedCenter) => centersMatchText(center, allowedCenter)));
}

function findCenterSummary(centers: Array<{ centerName?: string | null; completedCount?: number | null }>, centerName: string) {
  if (!centerName) return null;
  return centers.find((center) => centersMatchText(center.centerName || '', centerName)) || null;
}

function centersMatchText(a: string, b: string): boolean {
  return normalizeCenterText(a) === normalizeCenterText(b);
}

function normalizeCenterText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function formatCampaignPeriod(campaign: OfferCampaignConfig | null): string {
  if (!campaign?.startsAt && !campaign?.endsAt) return 'No date limit';
  const start = campaign.startsAt ? formatOfferCampaignDate(campaign.startsAt) : 'Open start';
  const end = campaign.endsAt ? formatOfferCampaignDate(campaign.endsAt) : 'Open end';
  return `${start} - ${end}`;
}

function formatDiscountRule(campaign: OfferCampaignConfig | null): string {
  if (!campaign) return '-';
  if (campaign.discountFormulaType === 'AGE_MULTIPLIER') return `Age x ${campaign.ageMultiplier}`;
  if (campaign.discountFormulaType === 'FIXED_AMOUNT') {
    return `${money(numberValue(campaign.themeConfig.fixedDiscountAmount) ?? numberValue(campaign.themeConfig.maximumDiscountCap))} fixed`;
  }
  return campaign.discountFormulaType || '-';
}

type CampaignBookingWindow = {
  key: string;
  startsAt: string;
  endsAt: string;
  centerRule?: { mode?: string; eligibleCenters?: unknown };
  patientRule?: { mode?: string; registrationFrom?: unknown; registrationTo?: unknown; existingRegisteredBefore?: unknown };
  advanceRule?: Record<string, unknown>;
};

type CampaignWindowAvailability = {
  configured: CampaignBookingWindow[];
  active: CampaignBookingWindow | null;
  next: CampaignBookingWindow | null;
  open: boolean;
};

function campaignWindowAvailability(campaign: OfferCampaignConfig | null, now = new Date()): CampaignWindowAvailability {
  const configured = (Array.isArray(campaign?.themeConfig?.bookingWindows) ? campaign?.themeConfig.bookingWindows : [])
    .map((value) => campaignBookingWindow(value))
    .filter((value): value is CampaignBookingWindow => Boolean(value))
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const timestamp = now.getTime();
  const campaignStarts = dateTimestamp(campaign?.startsAt);
  const campaignEnds = dateTimestamp(campaign?.endsAt);
  const withinCampaign = (campaignStarts === null || timestamp >= campaignStarts) && (campaignEnds === null || timestamp <= campaignEnds);
  if (!configured.length) return { configured, active: null, next: null, open: withinCampaign };
  const active = configured.find((window) => timestamp >= new Date(window.startsAt).getTime() && timestamp <= new Date(window.endsAt).getTime()) || null;
  const next = configured.find((window) => new Date(window.startsAt).getTime() > timestamp) || null;
  return { configured, active, next, open: withinCampaign && Boolean(active) };
}

function campaignBookingWindow(value: unknown): CampaignBookingWindow | null {
  const row = objectValue(value);
  const key = textValue(row.key);
  const startsAt = textValue(row.startsAt);
  const endsAt = textValue(row.endsAt);
  if (!key || !startsAt || !endsAt || dateTimestamp(startsAt) === null || dateTimestamp(endsAt) === null) return null;
  const centerRule = objectValue(row.centerRule);
  const patientRule = objectValue(row.patientRule);
  return {
    key,
    startsAt,
    endsAt,
    centerRule: { mode: textValue(centerRule.mode) || undefined, eligibleCenters: centerRule.eligibleCenters },
    patientRule: {
      mode: textValue(patientRule.mode) || undefined,
      registrationFrom: patientRule.registrationFrom,
      registrationTo: patientRule.registrationTo,
      existingRegisteredBefore: patientRule.existingRegisteredBefore,
    },
    advanceRule: objectValue(row.advanceRule),
  };
}

function summarizeCampaignRules(campaign: OfferCampaignConfig | null, windowState: CampaignWindowAvailability, centerCount: number) {
  const activeWindow = windowState.active;
  const patientRule = activeWindow?.patientRule || {};
  const patientMode = patientRule.mode || textValue(campaign?.themeConfig?.patientEligibilityMode) || 'INHERIT';
  const advanceRule = activeWindow?.advanceRule?.mode && activeWindow.advanceRule.mode !== 'INHERIT'
    ? activeWindow.advanceRule
    : objectValue(campaign?.requiredAdvanceAmountRule);
  const couponDuration = numberValue(campaign?.themeConfig?.couponValidityDuration);
  const couponUnit = textValue(campaign?.themeConfig?.couponValidityUnit);
  const treatmentDays = numberValue(campaign?.themeConfig?.packageStartWithinDays);
  return {
    bookingWindow: bookingWindowText(windowState),
    centers: centerRuleText(campaign, activeWindow, centerCount),
    patientRule: patientRuleText(patientMode, patientRule, campaign),
    advance: advanceRuleText(advanceRule, campaign),
    couponValidity: couponDuration && couponUnit ? `${couponDuration} ${couponUnit.toLowerCase()} from enrollment` : 'Not configured',
    treatmentStart: treatmentDays !== null ? `Within ${treatmentDays} days of enrollment` : 'Not configured',
  };
}

function centerRuleText(campaign: OfferCampaignConfig | null, activeWindow: CampaignBookingWindow | null, centerCount: number): string {
  const overall = stringList(campaign?.eligibleCenters);
  if (activeWindow?.centerRule?.mode === 'SUBSET') {
    return centerCount > 0 ? `${centerCount} center${centerCount === 1 ? '' : 's'} in ${activeWindow.key}` : `No center configured for ${activeWindow.key}`;
  }
  if (overall.length > 0) return `${centerCount || overall.length} campaign center${(centerCount || overall.length) === 1 ? '' : 's'}`;
  return 'All permitted centers';
}

function bookingWindowText(windowState: CampaignWindowAvailability): string {
  if (!windowState.configured.length) return windowState.open ? 'Campaign period is open' : 'Campaign period is closed';
  if (windowState.active) return `${bookingWindowLabel(windowState.active.key)}: ${formatRuleDateTime(windowState.active.startsAt)} - ${formatRuleDateTime(windowState.active.endsAt)}`;
  if (windowState.next) return `Closed now. ${bookingWindowLabel(windowState.next.key)} opens on ${formatRuleDateTime(windowState.next.startsAt)}`;
  return 'All configured booking windows are closed';
}

function bookingWindowLabel(value: string): string {
  const match = String(value || '').trim().match(/^WINDOW[_ -]?(\d+)$/i);
  return match ? `Booking window ${match[1]}` : String(value || 'Booking window').replaceAll('_', ' ').toLowerCase();
}

function patientRuleText(mode: string, rule: CampaignBookingWindow['patientRule'], campaign: OfferCampaignConfig | null): string {
  if (mode === 'NEW_IN_WINDOW') return 'New patients registered in this booking window';
  if (mode === 'EXISTING') return `Patients registered before ${formatRuleDate(rule?.existingRegisteredBefore)}`;
  if (mode === 'NEW_REGISTRATION_RANGE') return `Registered ${formatRuleDate(rule?.registrationFrom)} - ${formatRuleDate(rule?.registrationTo)}`;
  const from = campaign?.themeConfig?.registrationDateFrom;
  const to = campaign?.themeConfig?.registrationDateTo;
  if (from || to) return `Registered ${formatRuleDate(from)} - ${formatRuleDate(to)}`;
  return mode === 'ANY_PERMITTED_OVERALL' ? 'Any patient allowed by campaign rules' : 'Campaign-level patient rule';
}

function advanceRuleText(rule: Record<string, unknown>, campaign: OfferCampaignConfig | null): string {
  const mode = textValue(rule.mode) || textValue(rule.type) || campaign?.requiredAdvanceType || 'Not configured';
  if (mode === 'DISCOUNT_AMOUNT') return 'Same as eligible discount';
  if (mode === 'EXACT_AMOUNT') return `${money(numberValue(rule.amount))} exact`;
  if (mode === 'MINIMUM_AMOUNT') return `${money(numberValue(rule.minimum))} minimum`;
  if (mode === 'RANGE') return `${money(numberValue(rule.minimumAmount))} - ${money(numberValue(rule.maximumAmount))}`;
  return mode.replaceAll('_', ' ').toLowerCase();
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function numberValue(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dateTimestamp(value: unknown): number | null {
  const text = textValue(value);
  if (!text) return null;
  const parsed = new Date(text).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRuleDateTime(value: unknown): string {
  const timestamp = dateTimestamp(value);
  return timestamp === null ? '-' : formatOfferCampaignDateTime(new Date(timestamp).toISOString(), true);
}

function formatRuleDate(value: unknown): string {
  const text = textValue(value);
  return text ? formatOfferCampaignDate(text) : '-';
}

function workflowStepIndex(state: WorkflowState) {
  if (['ENTER_MRN', 'LOOKING_UP_MRN', 'NOT_FOUND'].includes(state)) return 0;
  if (['NOT_ELIGIBLE', 'ELIGIBLE', 'GENERATING_CODE'].includes(state)) return 1;
  if (state === 'ADVANCE_PENDING') return 2;
  if (['VERIFYING_ADVANCE', 'VERIFICATION_FAILED'].includes(state)) return 3;
  if (state === 'COMPLETED') return 4;
  return 0;
}

function businessErrorMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message.trim() : String(error || '').trim();
  if (!message) return fallback;
  if (/do not have access to this center/i.test(message)) return 'You do not have permission to enroll patients for the selected center.';
  if (/selected center is not eligible/i.test(message)) return 'The selected center is not included in this campaign. Choose an eligible center and try again.';
  if (/campaign slots are full/i.test(message)) return 'Campaign capacity has been reached. No new enrollment can be reserved at this time.';
  if (/already enrolled/i.test(message)) return message;
  if (/campaign is not active/i.test(message)) return 'This campaign is not active. Refresh the campaign details or contact the campaign administrator.';
  if (/outside|window|period.*closed/i.test(message) && /campaign|enrollment/i.test(message)) return 'Enrollment is currently closed. Review the next booking time shown in the campaign rules.';
  if (/source is not active|source is not.*mapped/i.test(message)) return 'This campaign is not ready for enrollment because a required patient or payment source is incomplete.';
  if (/^(internal server error|request failed)|status\s+\d+|prisma|sql|looker|stack|exception/i.test(message)) return fallback;
  return message.length <= 300 ? message : fallback;
}
